const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export function summarizeTraceEvents(events = []) {
  const rowsByKey = new Map();
  const failures = [];
  let finalTime = 0;

  (Array.isArray(events) ? events : []).forEach((event, index) => {
    if (!event || typeof event !== 'object') return;
    const meta = event.langgraph_v2 || {};
    const eventType = meta.event || event.event_type || event.type || 'event';
    const nodeId = event.node_id || meta.name || event.nodeId || 'flow';
    const runId = meta.run_id || event.run_id || '';
    const key = runId || nodeId || `event_${index}`;
    const t = asNumber(event.execution_time);
    if (t != null) finalTime = Math.max(finalTime, t);

    const row = rowsByKey.get(key) || {
      key,
      nodeId,
      runId,
      eventTypes: new Set(),
      tags: new Set(),
      status: 'running',
      eventCount: 0,
      llmEvents: 0,
      toolEvents: 0,
      startTime: null,
      endTime: null,
      duration: null,
      lastError: '',
    };

    row.eventCount += 1;
    row.eventTypes.add(eventType);
    (Array.isArray(meta.tags) ? meta.tags : []).forEach((tag) => row.tags.add(tag));
    if (t != null) {
      if (row.startTime == null || t < row.startTime) row.startTime = t;
      if (row.endTime == null || t > row.endTime) row.endTime = t;
    }
    if (event.node_duration != null) row.duration = Number(event.node_duration);
    if (eventType.includes('chat_model') || event.type === 'llm_token' || event.type === 'llm_end') row.llmEvents += 1;
    if (eventType.includes('tool') || event.type?.startsWith?.('tool_')) row.toolEvents += 1;
    if (event.type === 'node_complete' || eventType.endsWith('_end')) row.status = 'completed';
    if (event.type === 'node_error' || event.type === 'flow_error' || eventType.includes('error')) {
      row.status = 'error';
      row.lastError = event.error || event.message || event.data?.error || 'Unknown error';
      failures.push({ nodeId, eventType, error: row.lastError });
    }
    rowsByKey.set(key, row);
  });

  const rows = Array.from(rowsByKey.values()).map((row) => {
    const duration = row.duration != null
      ? row.duration
      : (row.startTime != null && row.endTime != null ? Math.max(0, row.endTime - row.startTime) : null);
    return {
      ...row,
      eventTypes: Array.from(row.eventTypes),
      tags: Array.from(row.tags),
      duration,
    };
  }).sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));

  const completedDurations = rows.map((row) => row.duration).filter((n) => Number.isFinite(n));
  const slowest = rows.reduce((acc, row) => {
    if (!Number.isFinite(row.duration)) return acc;
    if (!acc || row.duration > acc.duration) return row;
    return acc;
  }, null);

  return {
    rows,
    failures,
    totalTime: finalTime || completedDurations.reduce((sum, n) => sum + n, 0),
    slowestNode: slowest,
    averageNodeDuration: completedDurations.length
      ? completedDurations.reduce((sum, n) => sum + n, 0) / completedDurations.length
      : 0,
    eventCount: Array.isArray(events) ? events.length : 0,
  };
}

export function buildTraceFailureSuggestions(events = []) {
  const suggestions = [];
  const text = JSON.stringify(events || []).toLowerCase();
  const add = (id, title, detail) => {
    if (!suggestions.some((s) => s.id === id)) suggestions.push({ id, title, detail });
  };

  if (text.includes('structured') || text.includes('schema') || text.includes('json') || text.includes('parse')) {
    add('structured-output', 'Check structured output schema', 'Validate the LLM node schema and keep a deterministic normalizer before end.');
  }
  if (text.includes('401') || text.includes('403') || text.includes('unauthorized') || text.includes('forbidden') || text.includes('api key')) {
    add('auth', 'Check auth credentials', 'Confirm credential refs, tenant secrets, and API auth headers.');
  }
  if (text.includes('mcp') || text.includes('websocket') || text.includes('tool')) {
    add('mcp', 'Test MCP/tool connection', 'Use the MCP connection test and confirm toolName/arguments match the server.');
  }
  if (text.includes('flowref') || text.includes('outputpath') || text.includes('binding')) {
    add('binding', 'Check app bindings', 'Compare the final result JSON with panel outputPath and binding source paths.');
  }
  if (text.includes('model') || text.includes('provider') || text.includes('llm') || text.includes('quota')) {
    add('llm', 'Check LLM provider settings', 'Verify model name, provider, API key, rate limits, and structured-output support.');
  }
  if (!suggestions.length && (Array.isArray(events) ? events : []).some((ev) => ev?.type === 'flow_error' || ev?.type === 'node_error')) {
    add('generic', 'Inspect failed node payload', 'Open the event metadata and copy the debug bundle for backend logs.');
  }
  return suggestions;
}
